import LoaderWithBackdrop from '@/components/custom/LoaderWithBackdrop';
import { useNavigate, useParams } from "react-router-dom";

import { useArchiveProject } from "@/lib/useQuery/project/useArchiveProject";
import { useDeleteProject } from "@/lib/useQuery/project/useDeleteProject";
import { useRetrieveProject } from "@/lib/useQuery/project/useRetrieveProject";

import { UnderlineLink } from "@/components/atoms/UnderlineLink/UnderlineLink";
import DecisionModal from '@/components/custom/InfoPopups/DecisionModal';
import ErrorPopup from "@/components/custom/InfoPopups/ErrorPopup";
import SuccessPopup from "@/components/custom/InfoPopups/SuccessPopup";
import { ConfirmByTypingTextDialog } from "@/components/organisms/ConfirmByTypingTextDialog";
import { Button } from "@/components/ui/button";
import {
    Menubar,
    MenubarContent,
    MenubarItem,
    MenubarMenu,
    MenubarSeparator,
    MenubarTrigger
} from "@/components/ui/menubar";
import { OrganizationTypeWordingResolver } from "@/features/organization/components/atoms/OrganizationTypeWordingResolver";
import { CheckProjectAccess } from '@/features/rbac/components/molecules/CheckProjectAccess/CheckProjectAccess';
import { type UseProjectsListReturnData } from "@/lib/useQuery/project/useProjectsList";
import { Archive, ArchiveRestore, Info, MailPlus, Pencil, Settings, Trash2 } from "lucide-react";

type Props = {
    project: UseProjectsListReturnData[0] | null,
}

export const ProjectHeaderButtons = ({
    project,
}: Props) => {
    const { orgId, projectId } = useParams();
    const navigate = useNavigate();

    /*
    * Handle Archive, Restore and Delete Project
    */
    const { mutateAsync: archiveProject, isPending: isArchivingProject } = useArchiveProject();
    const { mutateAsync: restoreProject, isPending: isRetrievingProject } = useRetrieveProject();
    const { mutateAsync: deleteProject, isPending: isDeletingProject } = useDeleteProject();

    const restoreProjectHandler = async (project_id: string) => {
        const userDecision = await DecisionModal({ title: "Restore", confirmButtonText: "Restore", message: 'Do you really want to restore this project?' });
        if (!userDecision.isConfirmed) return;

        try {
            await restoreProject(project_id);
            SuccessPopup("Project restored successfully");
        } catch (error) {
            ErrorPopup("Error restoring survey");
        }
    };

    const archiveProjectHandler = async (project_id: string) => {
        const userDecision = await DecisionModal({ title: "Archive", confirmButtonText: "Archive", message: 'You will be able to restore this project later' });
        if (!userDecision.isConfirmed) return;

        try {
            await archiveProject(project_id);
            SuccessPopup("Project archived successfully");
        } catch (error) {
            ErrorPopup("Error archiving survey");
        }
    };

    const deleteProjectHandler = async (projectId: string) => {
        const userDecision = await DecisionModal({ title: "Delete", confirmButtonText: "Delete", message: 'Do you really want to delete this project? Once deleted, you will not be able to recover this project' });
        if (!userDecision.isConfirmed) return;
        try {
            await deleteProject(projectId);
            SuccessPopup("Project deleted successfully");
            navigate(`/organization/${orgId}`);
        } catch (error) {
            ErrorPopup("Error deleting project");
        }
    };

    if (!orgId || !projectId || !project) {
        return null;
    }

    return (
        <div>
            {(isArchivingProject || isRetrievingProject || isDeletingProject) && (<LoaderWithBackdrop />)}

            <Menubar>
                <MenubarMenu>
                    <MenubarTrigger asChild className='flex gap-2'>
                        <UnderlineLink to={`/organization/${orgId}/project/${projectId}/manage-project`}>
                            <Info size={16} /> <OrganizationTypeWordingResolver capitalize /> details
                        </UnderlineLink>
                    </MenubarTrigger>
                </MenubarMenu>
                <MenubarMenu>
                    <MenubarTrigger asChild className='flex gap-2'>
                        <UnderlineLink to={`/organization/${orgId}/project/${projectId}/manage-project/members`}>
                            <MailPlus size={16} /> Project members
                        </UnderlineLink>
                    </MenubarTrigger>
                </MenubarMenu>
                <MenubarMenu>
                    <CheckProjectAccess any project_update project_delete>
                        <MenubarTrigger asChild className='flex gap-2'>
                            <div className="text-xs">
                                <Settings size={16} /> Options
                            </div>
                        </MenubarTrigger>
                    </CheckProjectAccess>
                    <MenubarContent align='end'>
                        <CheckProjectAccess project_update>
                            <MenubarItem asChild className='flex gap-2'>
                                <UnderlineLink to={`/organization/${orgId}/project/${projectId}/manage-project/edit`}>
                                    <Pencil size={16} /> Edit
                                </UnderlineLink>
                            </MenubarItem>
                        </CheckProjectAccess>

                        <MenubarSeparator />

                        <CheckProjectAccess project_update>
                            {project?.archived ? (
                                <MenubarItem asChild className='flex justify-start w-full gap-2' >
                                    <Button className="text-xs" size='sm' variant="accent" onClick={() => restoreProjectHandler(projectId)}>
                                        <ArchiveRestore size={16} />Restore
                                    </Button>
                                </MenubarItem>
                            ) : (
                                <MenubarItem asChild className='flex justify-start w-full gap-2' >
                                    <Button className="text-xs" size='sm' variant="accent" onClick={() => archiveProjectHandler(projectId)}>
                                        <Archive size={16} /> Archive
                                    </Button>
                                </MenubarItem>
                            )}
                        </CheckProjectAccess>
                        <CheckProjectAccess project_delete>
                            <ConfirmByTypingTextDialog
                                onSubmit={() => {
                                    deleteProjectHandler(projectId)
                                }}
                                confirmText={project.name!}
                                dialogTrigger={
                                    <Button className="flex justify-start w-full gap-2 px-2 mt-1 text-xs" size='sm' variant='destructive'>
                                        <Trash2 size={16} /> Delete
                                    </Button>
                                }
                            />
                        </CheckProjectAccess>
                    </MenubarContent>
                </MenubarMenu>
            </Menubar>
        </div>
    )
}
